// Container Trader: seller plans (Free Classified, Verified Seller, and later Dealer Growth / Pro).
//
// A seller's plan is set by the operator in Sharetribe Console:
//   Users > (seller) > Extended data > Metadata
//     { "ctSellerPlan": "verified" }          -> approved Verified Seller (badge + 10 listings)
//     { "ctSellerPlan": "growth" | "pro" }    -> future dealer plans (badge + unlimited listings)
//     { "ctListingLimit": 25 }                -> optional per-seller override (number)
//     { "ctListingLimit": "unlimited" }       -> no limit (use for the Container Trader account)
//     { "ctPlanStatus": "past-due" }          -> optional account status shown on My Plan
//                                                ("active" | "past-due" | "suspended")
//     { "ctPlanRequestReviewedAt": "<ISO>",   -> operator marks the latest plan-change request
//       "ctPlanRequestOutcome": "declined",      as reviewed ("approved" | "declined"), with an
//       "ctPlanRequestNote": "..." }             optional note shown to the seller
//
// Plan-change requests are stored in the seller's privateData.ctPlanRequest by
// /api/seller-upgrade-request. A request never changes the plan; only metadata does.
// Metadata can only be changed by the operator, so sellers cannot give themselves a plan.
//
// Signing up as a "verified-seller" user type is an application. Until the operator sets
// ctSellerPlan, the account is treated as Free Classified (no badge, 3 listings).
//
// Listing limits are only enforced when REACT_APP_CT_LISTING_LIMITS=on, so the code can be
// deployed before every existing seller has been given the right plan in Console.

export const SELLER_PLAN_FREE = 'free';
export const SELLER_PLAN_VERIFIED = 'verified';
export const SELLER_PLAN_GROWTH = 'growth';
export const SELLER_PLAN_PRO = 'pro';

export const VERIFIED_APPLICANT_USER_TYPE = 'verified-seller';
// User types the plan limits apply to. Other user types (e.g. buyers, operator accounts) are
// never limited by this code.
export const LIMITED_USER_TYPES = ['provider', 'verified-seller'];

export const SELLER_PLANS = {
  [SELLER_PLAN_FREE]: {
    id: SELLER_PLAN_FREE,
    label: 'Free Classified',
    listingLimit: 3,
    monthly: '$0',
    setupFee: null,
    features: [
      'Up to 3 active classified listings',
      'Your own company profile',
      'Buyers contact you directly',
    ],
  },
  [SELLER_PLAN_VERIFIED]: {
    id: SELLER_PLAN_VERIFIED,
    label: 'Verified Seller',
    listingLimit: 10,
    monthly: '$69',
    setupFee: '$50',
    features: [
      'Everything in Free Classified',
      'Up to 10 active listings',
      'Verified Seller badge',
      'Your company on the Verified Sellers page',
    ],
  },
  [SELLER_PLAN_GROWTH]: {
    id: SELLER_PLAN_GROWTH,
    label: 'Dealer Growth',
    listingLimit: null,
    monthly: '$199',
    setupFee: '$100',
    features: [
      'Everything in Verified Seller',
      'Unlimited active listings',
      'Link to your dealer website',
      'One-click link to your own inventory or purchasing page',
      'Second-tier access to eligible online leads',
    ],
  },
  [SELLER_PLAN_PRO]: {
    id: SELLER_PLAN_PRO,
    label: 'Dealer Pro',
    listingLimit: null,
    monthly: '$249',
    setupFee: '$100',
    features: [
      'Everything in Dealer Growth',
      'Buy It Now eligibility for approved inventory',
      'Instant delivery estimates using your own rates',
      'Early access to eligible online leads',
      'Eligible to buy container inventory from wholesale suppliers',
      'Post loads on the load board and hire approved carriers',
    ],
  },
};

// Lowest to highest. Used to tell upgrades from downgrades.
export const PLAN_ORDER = [
  SELLER_PLAN_FREE,
  SELLER_PLAN_VERIFIED,
  SELLER_PLAN_GROWTH,
  SELLER_PLAN_PRO,
];

// Permissions each plan unlocks. Check these with hasPlanFeature() wherever a feature is gated,
// so the My Plan page and the real behaviour always come from the same table.
export const FEATURE_VERIFIED_BADGE = 'verifiedBadge';
export const FEATURE_DEALER_WEBSITE_LINK = 'dealerWebsiteLink';
export const FEATURE_LEADS_SECOND_TIER = 'leadsSecondTier';
export const FEATURE_LEADS_EARLY = 'leadsEarly';
export const FEATURE_BUY_IT_NOW = 'buyItNow';
export const FEATURE_OWN_DELIVERY_RATES = 'ownDeliveryRates';

export const PLAN_PERMISSIONS = {
  [SELLER_PLAN_FREE]: [],
  [SELLER_PLAN_VERIFIED]: [FEATURE_VERIFIED_BADGE],
  [SELLER_PLAN_GROWTH]: [
    FEATURE_VERIFIED_BADGE,
    FEATURE_DEALER_WEBSITE_LINK,
    FEATURE_LEADS_SECOND_TIER,
  ],
  [SELLER_PLAN_PRO]: [
    FEATURE_VERIFIED_BADGE,
    FEATURE_DEALER_WEBSITE_LINK,
    FEATURE_LEADS_EARLY,
    FEATURE_BUY_IT_NOW,
    FEATURE_OWN_DELIVERY_RATES,
  ],
};

export const planPriceText = plan =>
  plan.setupFee ? `${plan.monthly}/month + ${plan.setupFee} one-time setup` : 'Free';

export const planAllowanceText = plan =>
  plan.listingLimit == null
    ? 'Unlimited active listings'
    : `Up to ${plan.listingLimit} active listings`;

export const comparePlans = (fromId, toId) => PLAN_ORDER.indexOf(toId) - PLAN_ORDER.indexOf(fromId);

// The plan a seller would move up to from their current plan.
export const NEXT_PLAN = {
  [SELLER_PLAN_FREE]: SELLER_PLAN_VERIFIED,
  [SELLER_PLAN_VERIFIED]: SELLER_PLAN_GROWTH,
  [SELLER_PLAN_GROWTH]: SELLER_PLAN_PRO,
};

const VERIFIED_PLANS = [SELLER_PLAN_VERIFIED, SELLER_PLAN_GROWTH, SELLER_PLAN_PRO];

export const listingLimitsEnabled = () =>
  `${process.env.REACT_APP_CT_LISTING_LIMITS || ''}`.toLowerCase() === 'on';

const profileOf = user => user?.attributes?.profile || {};

export const getSellerPlanId = user => {
  const plan = `${profileOf(user).metadata?.ctSellerPlan || ''}`.trim().toLowerCase();
  return SELLER_PLANS[plan] ? plan : SELLER_PLAN_FREE;
};

export const getSellerPlan = user => SELLER_PLANS[getSellerPlanId(user)];

// Approved Verified Seller (or a higher dealer plan). Drives the Verified badge.
export const isVerifiedSeller = user => VERIFIED_PLANS.includes(getSellerPlanId(user));

// Signed up through the Verified Seller application but not yet approved.
export const isPendingVerifiedApplicant = user =>
  profileOf(user).publicData?.userType === VERIFIED_APPLICANT_USER_TYPE && !isVerifiedSeller(user);

/**
 * Returns the number of active listings the user may have, or null for no limit.
 * Active = published or pending approval. Closed listings and drafts do not count.
 */
export const getListingLimit = user => {
  if (!user?.id) return null;
  const { metadata = {}, publicData = {} } = profileOf(user);

  const override = metadata.ctListingLimit;
  if (override === 'unlimited' || override === null) return null;
  const overrideNumber = Number(override);
  if (override !== undefined && override !== '' && Number.isFinite(overrideNumber)) {
    return Math.max(0, Math.floor(overrideNumber));
  }

  // Sellers on a paid plan get the plan limit regardless of user type.
  if (metadata.ctSellerPlan && SELLER_PLANS[getSellerPlanId(user)]) {
    return getSellerPlan(user).listingLimit;
  }
  if (!LIMITED_USER_TYPES.includes(publicData.userType)) return null;
  return SELLER_PLANS[SELLER_PLAN_FREE].listingLimit;
};

// Thunk-style helper: counts the current user's active listings with the SDK.
export const countActiveOwnListings = sdk =>
  sdk.ownListings
    .query({ states: ['published', 'pendingApproval'], perPage: 1, page: 1 })
    .then(response => response?.data?.meta?.totalItems || 0);

/**
 * Resolves to { limit, count, reached, plan } for the current user.
 * reached is always false when limits are off or the user has no limit.
 */
export const checkListingLimit = (sdk, user) => {
  const plan = getSellerPlan(user);
  const limit = getListingLimit(user);
  if (!listingLimitsEnabled() || limit == null) {
    return Promise.resolve({ limit, count: null, reached: false, plan });
  }
  return countActiveOwnListings(sdk).then(count => ({
    limit,
    count,
    reached: count >= limit,
    plan,
  }));
};

// Feature permissions for the user's assigned plan (never from a pending request).
export const hasPlanFeature = (user, feature) =>
  (PLAN_PERMISSIONS[getSellerPlanId(user)] || []).includes(feature);

export const PLAN_STATUS_ACTIVE = 'active';
export const PLAN_STATUS_PENDING_APPROVAL = 'pending-approval';
export const PLAN_STATUS_PAST_DUE = 'past-due';
export const PLAN_STATUS_SUSPENDED = 'suspended';
const OPERATOR_STATUSES = [PLAN_STATUS_ACTIVE, PLAN_STATUS_PAST_DUE, PLAN_STATUS_SUSPENDED];

// Account status for My Plan. Operator-set metadata wins; otherwise applicants are pending.
export const getPlanStatus = user => {
  const status = `${profileOf(user).metadata?.ctPlanStatus || ''}`.trim().toLowerCase();
  if (OPERATOR_STATUSES.includes(status)) return status;
  return isPendingVerifiedApplicant(user) ? PLAN_STATUS_PENDING_APPROVAL : PLAN_STATUS_ACTIVE;
};

/**
 * The seller's latest plan-change request, or null.
 * Returns { plan, direction, message, requestedAt, state } where state is
 *   'pending'  - waiting for operator review
 *   'approved' - operator approved, or the plan already matches the request
 *   'declined' - operator declined (note in reviewNote)
 */
export const getPlanRequest = user => {
  const { privateData = {}, metadata = {} } = profileOf(user);
  const req = privateData.ctPlanRequest;
  if (!req || !SELLER_PLANS[req.plan] || !req.requestedAt) return null;
  const requestedAt = new Date(req.requestedAt).getTime();
  const reviewedAt = metadata.ctPlanRequestReviewedAt
    ? new Date(metadata.ctPlanRequestReviewedAt).getTime()
    : 0;
  let state = 'pending';
  if (getSellerPlanId(user) === req.plan) state = 'approved';
  else if (reviewedAt && reviewedAt >= requestedAt) {
    state = metadata.ctPlanRequestOutcome === 'approved' ? 'approved' : 'declined';
  }
  return {
    plan: req.plan,
    direction: comparePlans(getSellerPlanId(user), req.plan) > 0 ? 'upgrade' : 'downgrade',
    message: req.message || '',
    requestedAt: req.requestedAt,
    state,
    reviewNote: state === 'declined' ? metadata.ctPlanRequestNote || '' : '',
  };
};

/**
 * How many active listings must be closed before moving to targetPlanId.
 * Uses the target plan's own allowance (a per-seller ctListingLimit override is reviewed by the
 * operator separately). Returns 0 when the seller already fits.
 */
export const listingsOverAllowance = (activeCount, targetPlanId) => {
  const limit = SELLER_PLANS[targetPlanId]?.listingLimit;
  if (limit == null || activeCount == null) return 0;
  return Math.max(0, activeCount - limit);
};

export const LISTING_LIMIT_ERROR_TYPE = 'ct-listing-limit';
export const isListingLimitError = error => error?.type === LISTING_LIMIT_ERROR_TYPE;

// Accounts that have a seller plan to manage (shows the My Plan tab).
export const hasSellerPlanAccount = user => {
  const { publicData = {}, metadata = {} } = profileOf(user);
  return LIMITED_USER_TYPES.includes(publicData.userType) || !!metadata.ctSellerPlan;
};

// Dealer Buy It Now listing type. Create it in Sharetribe Console (Listings > Listing types) with
// this exact ID and a purchase transaction process. Only Dealer Pro sellers may use it.
// Override the ID with REACT_APP_CT_DEALER_BUY_IT_NOW_TYPE if you pick a different one.
// The Container Trader Managed Deals type ('container-trader-managed') is not affected.
export const DEALER_BUY_IT_NOW_LISTING_TYPE =
  `${process.env.REACT_APP_CT_DEALER_BUY_IT_NOW_TYPE || ''}`.trim() || 'dealer-buy-it-now';

// Dealer Pro plan with the Buy It Now permission, and the account is not suspended.
export const canUseDealerBuyItNow = user =>
  hasPlanFeature(user, FEATURE_BUY_IT_NOW) && getPlanStatus(user) !== PLAN_STATUS_SUSPENDED;

// Removes the dealer Buy It Now listing type for sellers who are not allowed to use it.
export const filterListingTypesForUser = (listingTypes = [], user) =>
  canUseDealerBuyItNow(user)
    ? listingTypes
    : listingTypes.filter(conf => conf?.listingType !== DEALER_BUY_IT_NOW_LISTING_TYPE);
